import { GL, GLSL_COMMON, Program, createBuffer } from '../gl';
import type { StarCatalog } from '../../data/catalog';
import { HECHT_ZERO, KS_RAYLEIGH } from '../../astro/skybrightness';
import { EXT_K, EYE_MAX, type EyeSky } from '../nakedeye';

/**
 * Naked eye's limit where a star is (nakedeye.ts, eyeLimitAt), written again for the shader: the sky's glow there from
 * its sources overhead (the models are cited in astro/skybrightness.ts), then the eye's limit against it
 * (eyeLimitFromSky), with tonight's nudge, at most the eye's cap. Its uniforms come from an EyeSky (eyeUniforms).
 */
const EYE_GLSL = /* glsl */ `
uniform vec4 u_eyeParts;     // shares of the light overhead: the natural sky, artificial light, twilight, the Moon
uniform vec4 u_eyeMoon;      // the Moon's direction (east, north, up) and 1 / its scattering where its glow is anchored
uniform vec4 u_eyeSky;       // the sky overhead (mag/arcsec²), tonight's nudge, the eye's cap, extinction (mag per air mass)
float log10f(float v) { return log2(v) * 0.30102999566; }
float exp10f(float v) { return exp2(v * 3.32192809489); }
float eyeFromSky(float S) {
  float night = 7.93 - 5.0 * log10f(exp10f(4.316 - S / 5.0) + 1.0);
  return max(night, ${HECHT_ZERO.toFixed(6)} - 5.0 * log10f(1.0 + sqrt(0.1259 * exp10f((12.58 - S) / 2.5))));
}
float eyeLimitAt(vec3 e) {
  float k = u_eyeSky.w;
  float c = max(e.z, 0.0);
  float X = inversesqrt(0.04 + 0.96 * c * c);
  float column = (1.0 - exp10f(-0.4 * k * X)) / (1.0 - exp10f(-0.4 * k));
  float share = u_eyeParts.x * X * exp10f(-0.4 * k * (X - 1.0)) + u_eyeParts.y * column + u_eyeParts.z;
  if (u_eyeParts.w > 0.0) {
    float cr = clamp(dot(e, u_eyeMoon.xyz), -1.0, 1.0);
    float rho = degrees(acos(cr));
    float mie = exp10f(6.15 - rho / 40.0);
    if (rho < 10.0) mie = max(mie, 6.2e7 / max(rho * rho, 0.0625));
    share += u_eyeParts.w * (${KS_RAYLEIGH.toFixed(3)} * (1.06 + cr * cr) + mie) * u_eyeMoon.w * column;
  }
  return min(u_eyeSky.z, eyeFromSky(u_eyeSky.x - 2.5 * log10f(share)) + u_eyeSky.y);
}`;

const VS = /* glsl */ `#version 300 es
precision highp float;
${GLSL_COMMON}
layout(location = 0) in vec3 a_dir;
layout(location = 1) in float a_mag;
layout(location = 2) in vec3 a_col;
uniform float u_magLimit;    // faintest magnitude drawn at this zoom and sky brightness
uniform float u_pxScale;     // point size multiplier (device pixels)
uniform float u_extK;        // extinction, magnitudes per air mass
uniform float u_time;
uniform float u_twinkle;
uniform float u_saturation;
uniform float u_highlight;   // index of a star to emphasise (-1 none)
uniform float u_below;       // 1: seeing through the ground, so stars below the horizon show (without the air's dimming)
uniform float u_eye;         // 1: Naked eye: just the stars the eye can see (as nakedeye.ts tests them), each plain enough to find
${EYE_GLSL}
out vec3 v_col;
out float v_alpha;
out float v_core;
out float v_halo;
out float v_glow;

float hash(float n) { return fract(sin(n * 12.9898) * 43758.5453); }

void main() {
  vec3 e = u_eqjToEnu * a_dir;
  gl_PointSize = 0.0;
  gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  bool under = e.z < -0.03;
  if (under && u_below < 0.5) return;
  float s = under ? 1.0 : max(e.z, 0.0);
  float X = 1.0 / (s + 0.025 * exp(-11.0 * s));        // air mass
  float m = a_mag + u_extK * (X - 1.0);
  vec3 p = projectCam(u_enuToCam * (under ? e : refractEnu(e)));
  if (p.z < 0.5) return;
  float rel = u_magLimit - m;                           // magnitudes above the limit
  // Naked eye: above the limit where the star is, against the sky's glow there (nakedeye.ts, eyeSeesAt).
  if (u_eye > 0.5 && !under) rel = eyeLimitAt(e) - m;
  if (rel < (u_eye > 0.5 ? 0.0 : -0.3)) return;
  // Core radius grows gently with brightness; bright stars get a wider halo. In Naked eye the faintest are a little
  // larger, so each named star is plain to find beside its name.
  float coreR = (u_eye > 0.5 ? clamp(0.95 + 0.33 * rel, 0.95, 3.6) : clamp(0.62 + 0.36 * rel, 0.6, 3.6)) * u_pxScale;
  float haloR = coreR * (2.2 + 0.55 * max(rel - 2.5, 0.0));
  float alpha = clamp((rel + 0.3) / 1.4, 0.0, 1.0);
  alpha *= alpha;
  // Every star on show in Naked eye carries a name, so none fades to nothing at the limit.
  if (u_eye > 0.5) alpha = clamp(0.55 + 0.4 * rel, 0.55, 1.0);
  // Scintillation: stronger near the horizon.
  float tw = u_twinkle * clamp((X - 1.0) * 0.12 + 0.03, 0.0, 0.35);
  float ph = hash(float(gl_VertexID)) * 6.2831;
  alpha *= 1.0 + tw * (sin(u_time * (9.0 + 5.0 * hash(float(gl_VertexID) + 3.0)) + ph) * 0.7 + sin(u_time * 17.3 + ph * 2.0) * 0.3);
  // Reddening near the horizon.
  vec3 col = a_col * vec3(1.0, exp(-0.03 * (X - 1.0)), exp(-0.08 * (X - 1.0)));
  float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
  float sat = u_saturation * clamp(0.55 + rel * 0.12, 0.55, 1.0);
  col = mix(vec3(l), col, sat);
  col = col / max(col.r, max(col.g, col.b));
  if (float(gl_VertexID) == u_highlight) { coreR *= 1.2; haloR *= 1.3; }
  float size = 2.0 * haloR + 2.0;
  v_col = col;
  v_alpha = alpha;
  v_core = coreR / size;
  v_halo = haloR / size;
  v_glow = clamp((rel - 1.5) * 0.12, 0.0, 0.45);
  gl_PointSize = clamp(size, 1.0, 64.0);
  gl_Position = vec4(p.xy, 0.0, 1.0);
}`;

const FS = /* glsl */ `#version 300 es
precision highp float;
in vec3 v_col;
in float v_alpha;
in float v_core;
in float v_halo;
in float v_glow;
out vec4 o;
void main() {
  float d = length(gl_PointCoord - 0.5);        // in units of the sprite size
  if (d > 0.5) discard;
  float c = d / v_core;
  float h = d / v_halo;
  float core = exp(-c * c * 1.6);
  float halo = exp(-h * h * 3.2) * (0.06 + v_glow);
  float i = (core + halo) * v_alpha;
  vec3 col = mix(v_col, vec3(1.0), core * 0.6);
  o = vec4(col * i, i);
}`;

/** EYE_GLSL's uniforms, from Naked eye's sky tonight. */
function eyeUniforms(p: Program, s: EyeSky) {
  p.f4('u_eyeParts', s.nat, s.art, s.tw, s.moon).f4('u_eyeMoon', s.mx, s.my, s.mz, s.moonRef).f4('u_eyeSky', s.sky, s.nudge, EYE_MAX, EXT_K);
}

/** One compiled program for every star layer (the Gaia tiles make many). */
const programs = new WeakMap<GL, Program>();

export class StarLayer {
  private prog: Program;
  private vao: WebGLVertexArrayObject;
  private buffers: WebGLBuffer[] = [];
  readonly count: number;

  constructor(private gl: GL, cat: StarCatalog) {
    let prog = programs.get(gl);
    if (!prog) programs.set(gl, (prog = new Program(gl, VS, FS, 'stars')));
    this.prog = prog;
    this.count = cat.count;
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    this.buffers.push(createBuffer(gl, cat.dir));
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
    this.buffers.push(createBuffer(gl, cat.mag));
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 1, gl.FLOAT, false, 0, 0);
    this.buffers.push(createBuffer(gl, cat.color));
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 3, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
  }

  /** `eye`: Naked eye's sky, to draw just the stars the eye can see (its limit overhead is `magLimit`); none in Full sky. */
  draw(setCommon: (p: Program) => void, u: {
    magLimit: number; pxScale: number; extK: number; time: number; twinkle: number; saturation: number; highlight: number; below?: number; eye?: EyeSky | null;
  }) {
    const gl = this.gl;
    const p = this.prog.use();
    setCommon(p);
    p.f1('u_magLimit', u.magLimit).f1('u_pxScale', u.pxScale).f1('u_extK', u.extK).f1('u_time', u.time);
    p.f1('u_twinkle', u.twinkle).f1('u_saturation', u.saturation).f1('u_highlight', u.highlight).f1('u_below', u.below ?? 0).f1('u_eye', u.eye ? 1 : 0);
    if (u.eye) eyeUniforms(p, u.eye);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.bindVertexArray(this.vao);
    // Stars are sorted brightest first, so drawing a prefix skips everything below the limit (in Naked eye, the
    // faintest limit anywhere on the sky).
    gl.drawArrays(gl.POINTS, 0, this.drawCount(u.eye ? u.eye.max : u.magLimit));
    gl.bindVertexArray(null);
  }

  /** Free the GPU buffers (tiles that have scrolled out of use). */
  dispose() {
    this.gl.deleteVertexArray(this.vao);
    for (const b of this.buffers) this.gl.deleteBuffer(b);
    this.buffers = [];
  }

  private magSorted: Float32Array | null = null;
  setMagnitudes(mag: Float32Array) {
    this.magSorted = mag;
  }
  private drawCount(limit: number) {
    const m = this.magSorted;
    if (!m) return this.count;
    // Allow for the extinction and twinkle margin.
    let lo = 0, hi = m.length;
    const target = limit + 0.4;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (m[mid] <= target) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }
}
